import Frame from "../../../../components/elements/Frame";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Divider, Icon, Image, SheetContainer, Stack, Text, Timeline } from "@bookingcom/bui-react";
import { useDispatch, useSelector } from "react-redux";

import styles from "./WhitelabelAncillaryBanner.module.css";
import useContextName from "hooks/useContextName";
import { AppDispatch } from "store";
import { whitelabelAncillaryService } from "ancillaries/service/whitelabelAncillariesService";
import useGlobalContext from "hooks/useGlobalContext";
import usePointOfSale from "hooks/usePointOfSale";
import { getGeniusLevel } from "store/user/selectors";
import { getSearchSegments } from "store/search/selectors";
import { getFlightSegments, getOrder, getOrderCurrency, getOrderStatus } from "store/order/selectors";
import { pastBookingUtils } from "@flights/web-api-utils-universal";
import { SkeletonListMultiLine } from "components/elements/Skeleton/SkeletonListMultiLine";
import flights_ace_web_lounge_access_post_booking from "utils/experiments/ace/flights_ace_web_lounge_access_post_booking";
import flights_ace_web_airport_parking_post_booking from "utils/experiments/ace/flights_ace_web_airport_parking_post_booking";
import { getFlightDetailsSegments } from "store/flightDetails/selectors";
import {
  useTrackWhitelabelLoadBanner,
  useTrackWhitelabelViewBanner,
  useWhitelabelClickBanner
} from "ancillaries/hooks/Whitelabel/useWhiteLabelTrackV2";
import useIsInViewport from "hooks/useIsInViewport";
import {
  whitelabelAncillariesLoadingSelector,
  whitelabelAncillariesSelector
} from "ancillaries/store/whitelabelAncillaries/selectors";
import {
  BellNormalIcon,
  CreditCardFrontIcon,
  MagnifyingGlassIcon,
  ParkingSignIcon,
  PopoutIcon,
  SparklesIcon
} from "@bookingcom/bui-assets-react/streamline";
interface IWhitelabelAncillaryBannerProps {
  ancillary: "loungeAccess" | "airportParking";
  showHeader?: boolean;
}

export const WhitelabelAncillaryBanner = ({ ancillary, showHeader }: IWhitelabelAncillaryBannerProps) => {
  const whitelabelAncillariesLoading = useSelector(whitelabelAncillariesLoadingSelector);
  const whitelabelAncillaries = useSelector(whitelabelAncillariesSelector);
  const ancillaryData = whitelabelAncillaries?.whitelabelAncillaries?.find((current) => {
    return current.ancillary === ancillary;
  });
  const isAncillaryEligible = !!ancillaryData;
  const heading = ancillaryData?.heading;
  const title = ancillaryData?.title;
  const description = ancillaryData?.description;
  const information = ancillaryData?.information;
  const usp = ancillaryData?.usp;
  const link = ancillaryData?.cta?.link;
  const ctaText = ancillaryData?.cta?.text;
  const ctaA11yText = ancillaryData?.cta?.a11yText;
  const secondaryAction = ancillaryData?.secondary_action;
  const footer = ancillaryData?.footer;
  const showButton = !!link;
  const showSecondaryAction = !!secondaryAction;

  // Call the service to fetch the whitelabel ancillary data
  const dispatch = useDispatch<AppDispatch>();
  const { features } = useGlobalContext();
  const contextName = useContextName();
  const geniusLevel = useSelector(getGeniusLevel)?.toString();
  const pointOfSale = usePointOfSale();
  const searchSegments = useSelector(getSearchSegments);
  const order = useSelector(getOrder);
  const orderSegments = useSelector(getFlightSegments);
  const flightDetailsSegments = useSelector(getFlightDetailsSegments);
  const orderStatus = useSelector(getOrderStatus);
  const orderCurrency = useSelector(getOrderCurrency);

  const whitelabelAncillaryFeature = !!features["FLIGHTS_ACE_WHITELABEL_ANCILLARY_API"];
  const whitelabelAncillaryDisplayFeature = !!features["FLIGHTS_ACE_WHITELABEL_ANCILLARY_DISPLAY"];
  const isPostBooking = contextName === "confirmation" || contextName === "pb_order_details";
  const isPastBooking = pastBookingUtils.isPastBooking(order);
  const eligibleBooking = !isPostBooking || orderStatus === "CONFIRMED" || orderStatus === "PENDING";
  const bannerRef = useRef<HTMLDivElement>(null);
  const isBannerInViewport = useIsInViewport(bannerRef, [ancillaryData]);
  const trackWhiteLabelClickBanner = useWhitelabelClickBanner(ancillary, contextName);

  const handleButtonClick = () => {
    trackWhiteLabelClickBanner();
    if (ancillary === "loungeAccess") {
      flights_ace_web_lounge_access_post_booking.goals.lounge_button_clicked();
    } else if (ancillary === "airportParking") {
      flights_ace_web_airport_parking_post_booking.goals.airport_parking_button_clicked();
    }
    window.open(link, "_blank", "noopener noreferrer");
  };

  const [showSecondaryActionModal, setSecondaryActionModal] = useState(false);

  const handleSecondaryButtonClick = () => {
    flights_ace_web_airport_parking_post_booking.goals.airport_parking_how_it_works_clicked();
    if (secondaryAction?.cta?.action === "LINK") {
      window.open(secondaryAction?.cta?.link, "_blank", "noopener noreferrer");
    } else {
      setSecondaryActionModal(true);
    }
  };

  const onCloseSecondaryActionModal = () => {
    setSecondaryActionModal(false);
  };

  const getSegments = () => {
    if (contextName === "search_results") {
      return searchSegments;
    } else if (isPostBooking) {
      return orderSegments;
    } else {
      return flightDetailsSegments;
    }
  };

  const segments = getSegments();

  useTrackWhitelabelLoadBanner(ancillary, showButton, contextName);
  useTrackWhitelabelViewBanner(ancillary, showButton, isBannerInViewport, contextName);

  useEffect(() => {
    const fetchWhitelabels = async () => {
      if (!segments) {
        return;
      }
      await dispatch(
        whitelabelAncillaryService.getWhitelabels({
          geniusLevel,
          pointOfSale,
          segments,
          contextName,
          orderStatus,
          orderCurrency
        })
      );
    };
    if (whitelabelAncillaryFeature && pointOfSale && eligibleBooking && !isPastBooking) {
      void fetchWhitelabels();
    }
  }, [
    contextName,
    dispatch,
    whitelabelAncillaryFeature,
    geniusLevel,
    pointOfSale,
    segments,
    orderStatus,
    eligibleBooking,
    isPastBooking,
    orderCurrency
  ]);

  const getIllustrationAsset = useCallback(() => {
    if (ancillary === "loungeAccess") {
      return "FlightsLoungeAccess";
    } else if (ancillary === "airportParking") {
      return "AirportParking";
    }
    return "NotificationsEmptyState";
  }, [ancillary]);

  const getTimelineIcons = (icon: string) => {
    if (icon === "search") {
      return MagnifyingGlassIcon;
    } else if (icon === "pay") {
      return CreditCardFrontIcon;
    } else if (icon === "notification") {
      return BellNormalIcon;
    } else if (icon === "parking") {
      return ParkingSignIcon;
    }
    return SparklesIcon;
  };

  const secondaryActionsList = useMemo(() => {
    return secondaryAction?.information_list.length
      ? secondaryAction?.information_list?.map((item, index) => (
          <Timeline.Item
            key={index}
            marker={<Icon svg={getTimelineIcons(item.icon)}></Icon>}
            title={
              <Text
                tagName="div"
                variant="strong_1"
                className={styles.timelineItemTitle}
                attributes={{
                  "aria-label": item.title
                }}
              >
                {item.title}
              </Text>
            }
          >
            <Text tagName="span" variant="body_2" color="neutral_alt">
              {item.description}
            </Text>
          </Timeline.Item>
        ))
      : null;
  }, [secondaryAction]);

  // Hide the banner if the feature is disabled or not in PB
  if (!whitelabelAncillaryDisplayFeature || !isPostBooking) {
    return null;
  }

  // Hide the banner if in PB and not in lounge access experiment variant
  if (
    isPostBooking &&
    isAncillaryEligible &&
    ancillary === "loungeAccess" &&
    !flights_ace_web_lounge_access_post_booking.trackWithDefaultStage()
  ) {
    return null;
  }
  // Hide the banner if in PB and not in Airport parking experiment variant
  if (
    isPostBooking &&
    isAncillaryEligible &&
    ancillary === "airportParking" &&
    !flights_ace_web_airport_parking_post_booking.trackWithDefaultStage()
  ) {
    return null;
  }

  // Show skeleton loading
  if (whitelabelAncillariesLoading) {
    return (
      <Card>
        <Stack>
          <SkeletonListMultiLine amount={1} />
        </Stack>
      </Card>
    );
  }

  // Track banner shown
  if (isAncillaryEligible) {
    if (ancillary === "loungeAccess") {
      flights_ace_web_lounge_access_post_booking.goals.lounge_access_banner_shown();
    } else if (ancillary === "airportParking") {
      flights_ace_web_airport_parking_post_booking.goals.airport_parking_banner_shown();
    }
  }

  return isAncillaryEligible ? (
    <React.Fragment>
      <Stack direction={"column"} gap={4} attributes={{ ref: bannerRef }}>
        {showHeader && heading ? <Text variant={"headline_3"}>{heading}</Text> : null}
        <Card>
          <Stack>
            <Frame mb={1} direction="row" alignItems="start">
              <Frame pr={3} pt={1}>
                <Image
                  width="64px"
                  height="64px"
                  alt={title}
                  asset={{
                    setName: "illustrations-traveller",
                    assetName: getIllustrationAsset()
                  }}
                  contentMode="fit"
                />
              </Frame>
              <Stack>
                <Text variant="strong_1">{title}</Text>
                <Text variant="body_1">{description}</Text>
                <Stack>
                  <Stack direction={"row"} gap={2}>
                    {usp ? (
                      <Stack direction={"row"} gap={2} alignItems={"center"}>
                        <Text variant="small_1" color="constructive">
                          {usp}
                        </Text>
                        <Text variant="small_1" color="neutral_alt">
                          |
                        </Text>
                      </Stack>
                    ) : null}
                    <Text variant="small_1" color="neutral_alt">
                      {information}
                    </Text>
                  </Stack>
                  <Stack direction={"row"} gap={4}>
                    {showButton ? (
                      <Button
                        attributes={{ "aria-label": ctaA11yText }}
                        variant="secondary"
                        size="medium"
                        onClick={handleButtonClick}
                        className={styles.ctaButton}
                      >
                        {ctaText}
                      </Button>
                    ) : null}
                    {showSecondaryAction ? (
                      <Button
                        attributes={{ "aria-label": secondaryAction?.cta?.a11yText }}
                        variant="tertiary"
                        size="medium"
                        onClick={handleSecondaryButtonClick}
                        className={styles.ctaButton}
                      >
                        {secondaryAction?.cta?.text}
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>
            </Frame>
            {footer ? (
              <React.Fragment>
                <Divider />
                <Frame mt={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Text variant="small_2" color="neutral_alt">
                    {footer}
                  </Text>
                  <Image
                    asset={{
                      setName: "images-genius",
                      assetName: "GeniusLogo"
                    }}
                    height="14px"
                    width="54px"
                    alt="Genius Logo"
                    contentMode="fit"
                  />
                </Frame>
              </React.Fragment>
            ) : null}
          </Stack>
        </Card>
      </Stack>
      <SheetContainer
        active={showSecondaryActionModal}
        onCloseTrigger={onCloseSecondaryActionModal}
        position="center"
        closeAriaLabel={secondaryAction?.cta?.a11yTextCloseModal || "Close modal"}
        title={secondaryAction?.title}
        subtitle={secondaryAction?.subtitle}
      >
        <Stack direction={"column"} gap={4} className={styles.modalContainer}>
          <Timeline>{secondaryActionsList}</Timeline>
          <Stack direction={"row"} justifyContent="start" alignItems={"center"} gap={4}>
            <Button text={ctaText} onClick={handleButtonClick} iconPosition="end" icon={<PopoutIcon />} />
            <Text variant="small_2" color="neutral_alt">
              {information}
            </Text>
          </Stack>
        </Stack>
      </SheetContainer>
    </React.Fragment>
  ) : null;
};
